import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Flex, Text, Box, xcss } from '@atlaskit/primitives';
import { IconButton } from '@atlaskit/button/new';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import Tooltip from '@atlaskit/tooltip';

import type { ContentVersionHistoryQuery_contentVersionHistory_nodes as VersionHistoryNode } from '../graphql/__types__/ContentVersionHistoryQuery';

import { getFormattedDate } from './versionHistoryUtils';

const i18n = defineMessages({
	copyIconLabel: {
		id: 'whiteboard-common.copy.icon.label',
		defaultMessage: 'Copy icon',
		description: 'aria-label for copy icon',
	},
	copyIconTooltip: {
		id: 'whiteboard-common.copy.icon.tooltip',
		defaultMessage: 'Copy all content',
		description: 'tooltip text displayed on the copy icon before clicking',
	},
	copyIconTooltipCopied: {
		id: 'whiteboard-common.copy.icon.tooltip-copied',
		defaultMessage: 'Copied!',
		description: 'tooltip text displayed on the copy button after user has clicked',
	},
});

const selectedItemStyles = xcss({
	borderLeftColor: 'color.border.selected',
	borderLeftWidth: '2px',
	borderLeftStyle: 'solid',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
		cursor: 'pointer',
	},
});

const itemStyles = xcss({
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		cursor: 'pointer',
	},
});

type VersionHistoryNodeType = {
	version: VersionHistoryNode;
	handleVersionClick: (version: VersionHistoryNode) => void;
	isSelected: boolean;
	isCopyAllowed: boolean;
	handleCopyClick: () => void;
};

export const VersionHistoryItem = ({
	version,
	handleVersionClick,
	isSelected,
	isCopyAllowed,
	handleCopyClick,
}: VersionHistoryNodeType) => {
	const { formatMessage, formatDate, formatTime } = useIntl();
	const copyAllContentText = formatMessage(i18n.copyIconTooltip);
	const copiedText = formatMessage(i18n.copyIconTooltipCopied);
	const [isCopyClicked, setIsCopyClicked] = useState(false);

	const handleClick = useCallback(() => {
		handleCopyClick();
		setIsCopyClicked(true);
	}, [handleCopyClick]);

	const handleMouseLeave = () => {
		setIsCopyClicked(false);
	};

	return (
		<Box
			paddingBlock="space.100"
			paddingInlineEnd="space.150"
			paddingInlineStart="space.300"
			data-testId="version-history-item"
			backgroundColor={isSelected ? 'color.background.selected' : 'elevation.surface'}
			onClick={() => handleVersionClick(version)}
			xcss={[itemStyles, isSelected && selectedItemStyles]}
		>
			<Flex direction="row" alignItems="center" justifyContent="space-between">
				<Text size="medium">{getFormattedDate(version.updatedTime, formatDate, formatTime)}</Text>
				{isSelected && isCopyAllowed && (
					<Tooltip
						content={isCopyClicked ? copiedText : copyAllContentText}
						position="bottom"
						// force remount otherwise content is off-center
						key={isCopyClicked ? 'copied' : 'default'}
					>
						{(tooltipProps) => (
							<IconButton
								{...tooltipProps}
								appearance="subtle"
								icon={CopyIcon}
								label={formatMessage(i18n.copyIconLabel)}
								onClick={handleClick}
								onMouseLeave={handleMouseLeave}
								onBlur={handleMouseLeave}
							/>
						)}
					</Tooltip>
				)}
			</Flex>
		</Box>
	);
};
